<template>
    <v-container fluid class="py-8">
        <!-- Header -->
        <v-row class="mb-4" align="center">
            <!-- Left: Back -->
            <v-col cols="auto">
                <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="$router.back()">
                    Back
                </v-btn>
            </v-col>

            <!-- Center: Title (truly centered) -->
            <v-col cols="12" md="8" class="text-center mx-auto">
                <span class="text-overline text-medium-emphasis d-block mb-1">Agent</span>
                <h1 class="text-h5 font-weight-bold">{{ displayName(agent) }}</h1>
                <div class="text-caption text-medium-emphasis">UID: {{ agent?.uid || '—' }}</div>
            </v-col>

            <!-- Right: Ghost button to balance left width (keeps center perfectly centered) -->
            <v-col cols="auto" class="d-none d-md-flex">
                <v-btn variant="text" prepend-icon="mdi-arrow-left" style="visibility:hidden">Back</v-btn>
            </v-col>
        </v-row>

        <!-- Summary Cards -->
        <v-row v-if="!isLoading.agent" class="mb-6" align="stretch">
            <!-- Total Paid to Agent -->
            <v-col cols="12" md="4">
                <v-card variant="tonal" color="teal" elevation="2" class="h-100">
                    <v-card-text class="py-5 d-flex align-center justify-space-between">
                        <div>
                            <div class="text-overline text-medium-emphasis">Total Paid to Agent</div>
                            <div class="text-h5 font-weight-bold text-teal-darken-2">
                                {{ formatCurrency(summary.total_payout) }}
                            </div>
                        </div>
                        <v-avatar size="56" color="teal" variant="tonal" class="summary-icon">
                            <v-icon size="34">mdi-cash-multiple</v-icon>
                        </v-avatar>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Platform Fees Paid -->
            <v-col cols="12" md="4">
                <v-card variant="tonal" color="deep-orange" elevation="2" class="h-100">
                    <v-card-text class="py-5 d-flex align-center justify-space-between">
                        <div>
                            <div class="text-overline text-medium-emphasis">Platform Fees Paid</div>
                            <div class="text-h5 font-weight-bold text-deep-orange-darken-2">
                                {{ formatCurrency(summary.total_fees) }}
                            </div>
                        </div>
                        <v-avatar size="56" color="deep-orange" variant="tonal" class="summary-icon">
                            <v-icon size="34">mdi-cash-minus</v-icon>
                        </v-avatar>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Listings -->
            <v-col cols="12" md="4">
                <v-card variant="tonal" color="indigo" elevation="2" class="h-100">
                    <v-card-text class="py-5 d-flex align-center justify-space-between">
                        <div>
                            <div class="text-overline text-medium-emphasis">Listings</div>
                            <div class="text-h5 font-weight-bold text-indigo-darken-2">
                                {{ summary.listing_count }}
                            </div>
                        </div>
                        <v-avatar size="56" color="indigo" variant="tonal" class="summary-icon">
                            <v-icon size="34">mdi-format-list-bulleted</v-icon>
                        </v-avatar>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>


        <v-row v-else class="mb-6">
            <v-col cols="12" md="4"><v-skeleton-loader type="card" /></v-col>
            <v-col cols="12" md="4"><v-skeleton-loader type="card" /></v-col>
            <v-col cols="12" md="4"><v-skeleton-loader type="card" /></v-col>
        </v-row>

        <!-- Tabs -->
        <v-card elevation="2">
            <v-card-text>
                <v-tabs v-model="tab" color="primary">
                    <v-tab value="info">Info</v-tab>
                    <v-tab value="docs">Documents</v-tab>
                    <v-tab value="listings">Listings</v-tab>
                    <v-tab value="chat">Chat</v-tab>
                </v-tabs>

                <v-divider class="my-2" />

                <v-window v-model="tab">
                    <!-- Info tab -->
                    <v-window-item value="info">
                        <v-row class="gy-4">
                            <!-- Left column: full-width sheet (no card) -->
                            <v-col cols="12" md="4">
                                <v-sheet color="surface" class="pa-4 rounded-lg w-100">
                                    <div class="d-flex align-center mb-4">
                                        <v-avatar size="56" class="mr-3">
                                            <v-img :src="agent?.profile_picture || 'https://placehold.co/100'" />
                                        </v-avatar>
                                        <div>
                                            <div class="text-subtitle-1 font-weight-medium">{{ displayName(agent) }}
                                            </div>
                                        </div>
                                    </div>

                                    <v-list density="compact">
                                        <v-list-item>
                                            <v-list-item-title class="text-medium-emphasis">Email</v-list-item-title>
                                            <v-list-item-subtitle>{{ agent?.email || '—' }}</v-list-item-subtitle>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-title class="text-medium-emphasis">Phone</v-list-item-title>
                                            <v-list-item-subtitle>{{ agent?.phone || '—' }}</v-list-item-subtitle>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-title class="text-medium-emphasis">Agency</v-list-item-title>
                                            <v-list-item-subtitle>{{ agent?.agency_name || '—' }}</v-list-item-subtitle>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-title class="text-medium-emphasis">ITA
                                                Number</v-list-item-title>
                                            <v-list-item-subtitle>{{ agent?.ita_number || '—' }}</v-list-item-subtitle>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-title class="text-medium-emphasis">Location</v-list-item-title>
                                            <v-list-item-subtitle>{{ readableLocation(agent) }}</v-list-item-subtitle>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-title class="text-medium-emphasis">Approved</v-list-item-title>
                                            <v-list-item-subtitle>
                                                {{ agent?.agent_approved_at ? formatDateTime(agent.agent_approved_at) :
                                                    '—' }}
                                            </v-list-item-subtitle>
                                        </v-list-item>
                                    </v-list>
                                </v-sheet>
                            </v-col>

                            <!-- Right column: alert + notes as sheets (no card) -->
                            <v-col cols="12" md="8">
                                <v-alert type="info" variant="tonal" class="mb-4" v-if="!agent?.is_agent">
                                    This user is not marked as an Agent yet.
                                </v-alert>

                                <v-sheet color="surface" class="pa-4 rounded-lg w-100">
                                    <div class="text-subtitle-1 font-weight-medium mb-2">Notes</div>
                                    <div class="text-medium-emphasis">
                                        <!-- Placeholder for internal notes UI -->
                                        Add internal notes UI here if needed.
                                    </div>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-window-item>

                    <!-- Documents tab -->
                    <v-window-item value="docs" class="my-6">
                        <!-- Force NO horizontal gap; keep 16px vertical gap -->
                        <v-row align="stretch" class="docs-row">
                            <v-col cols="12" sm="6" md="4">
                                <v-card elevation="1" class="h-100">
                                    <v-card-title>Advisor Agreement</v-card-title>
                                    <v-card-text>
                                        <template v-if="agent?.advisor_agreement_url">
                                            <v-btn :href="agent.advisor_agreement_url" target="_blank"
                                                block>Open</v-btn>
                                        </template>
                                        <template v-else>
                                            <div class="text-medium-emphasis">No file on record.</div>
                                        </template>
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <v-col cols="12" sm="6" md="4">
                                <v-card elevation="1" class="h-100">
                                    <v-card-title>Travel License</v-card-title>
                                    <v-card-text>
                                        <template v-if="agent?.travel_license_url">
                                            <v-btn :href="agent.travel_license_url" target="_blank" block>Open</v-btn>
                                        </template>
                                        <template v-else>
                                            <div class="text-medium-emphasis">No file on record.</div>
                                        </template>
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <v-col cols="12" sm="6" md="4">
                                <v-card elevation="1" class="h-100">
                                    <v-card-title>W-9</v-card-title>
                                    <v-card-text>
                                        <template v-if="agent?.w9_url">
                                            <v-btn :href="agent.w9_url" target="_blank" block>Open</v-btn>
                                        </template>
                                        <template v-else>
                                            <div class="text-medium-emphasis">No file on record.</div>
                                        </template>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-window-item>

                    <!-- Listings tab -->
                    <v-window-item value="listings">
                        <v-card elevation="1">
                            <v-card-title>
                                Listings
                                <v-spacer />
                                <small class="text-medium-emphasis">Showing {{ listPageStart + 1 }}–{{ listPageEnd }} of
                                    {{ listingsCount
                                    }}</small>
                            </v-card-title>
                            <v-card-text>
                                <v-table>
                                    <thead>
                                        <tr>
                                            <th class="text-left">Itinerary</th>
                                            <th class="text-left">Sail Date</th>
                                            <th class="text-left">Total</th>
                                            <th class="text-left">Service Fee</th>
                                            <th class="text-left">Payout</th>
                                            <th class="text-left">Status</th>
                                            <th class="text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="l in listings" :key="l.id">
                                            <td>{{ l.itinerary?.title || '—' }}</td>
                                            <td>{{ formatDate(l.itinerary?.date) }}</td>
                                            <td>{{ formatCurrency(l.total_cost) }}</td>
                                            <td>{{ formatCurrency(l.service_fee) }}</td>
                                            <td>{{ formatCurrency((Number(l.total_cost) || 0) -
                                                (Number(l.service_fee) || 0)) }}</td>
                                            <td>{{ l.status || '—' }}</td>
                                            <td class="text-right">
                                                <v-btn size="small" variant="outlined" color="secondary"
                                                    :to="`/dashboard/listings/admin/${l.id}`">View</v-btn>
                                            </td>
                                        </tr>
                                        <tr v-if="(listings || []).length === 0">
                                            <td colspan="7" class="text-center text-medium-emphasis py-6">No listings
                                                found.</td>
                                        </tr>
                                    </tbody>
                                </v-table>

                                <v-row class="mt-4" justify="end">
                                    <v-col cols="auto">
                                        <v-pagination v-model="listPage" :length="listTotalPages" :total-visible="5"
                                            prev-icon="mdi-chevron-left" next-icon="mdi-chevron-right"
                                            @update:modelValue="fetchListings" />
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-window-item>

                    <!-- Chat Tab-->
                    <v-window-item value="chat">
                        <div v-if="userId && uid">
                            <ChatRoom :user-id="userId" :other-user-id="uid" mode="dm" />
                        </div>
                    </v-window-item>
                </v-window>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import ChatRoom from '@/components/ChatRoom.vue'

export default {
    name: 'AgentDetail',
    components: {
        ChatRoom
    },
    data() {
        return {
            tab: 'info',
            uid: null,
            agent: null,
            userId: this.$store.state.user?.uid,

            summary: { total_payout: 0, total_fees: 0, listing_count: 0 },

            listings: [],
            listPage: 1,
            listPageSize: 10,
            listingsCount: 0,
            listTotalPages: 1,

            isLoading: {
                agent: true,
                summary: true,
                listings: true,
            },
        }
    },
    computed: {
        listPageStart() {
            return (this.listPage - 1) * this.listPageSize
        },
        listPageEnd() {
            return Math.min(this.listPageStart + this.listPageSize, this.listingsCount || 0)
        },
    },
    methods: {
        displayName(u) {
            if (!u) return '—'
            const fn = u.first_name?.trim()
            const ln = u.last_name?.trim()
            return fn || ln ? [fn, ln].filter(Boolean).join(' ') : (u.email || '—')
        },
        readableLocation(u) {
            if (!u) return '—'
            const parts = [u.city, u.region, u.postal_code].filter(Boolean)
            return parts.length ? parts.join(', ') : '—'
        },
        formatDate(d) {
            if (!d) return '—'
            const dt = new Date(d)
            return isNaN(dt) ? '—' : dt.toLocaleDateString()
        },
        formatDateTime(d) {
            if (!d) return '—'
            const dt = new Date(d)
            return isNaN(dt) ? '—' : dt.toLocaleString()
        },
        formatCurrency(v) {
            const n = Number(v) || 0
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
        },

        async load() {
            this.uid = this.$route.params.uid
            await Promise.all([this.fetchAgent(), this.fetchSummary(), this.fetchListings(this.listPage)])
        },

        async fetchAgent() {
            this.isLoading.agent = true
            try {
                const { data, error } = await supabase
                    .from('users')
                    .select(`
            uid, first_name, last_name, email, phone, created_at,
            "isAdmin", paid_user, profile_picture, is_agent,
            agency_name, ita_number, city, region, postal_code,
            advisor_agreement_url, travel_license_url, w9_url,
            agent_approved_at, agent_approved_by
          `)
                    .eq('uid', this.uid)
                    .single()

                if (error) throw error
                this.agent = data
            } catch (err) {
                console.error('fetchAgent error:', err)
                this.agent = null
            } finally {
                this.isLoading.agent = false
            }
        },

        async fetchSummary() {
            this.isLoading.summary = true
            try {
                // Preferred: RPC (see SQL below). Falls back to client-side sum if RPC not available.
                const { data, error } = await supabase
                    .rpc('agent_financial_summary', { p_agent_uid: this.uid })

                if (!error && data && data.length) {
                    const row = data[0]
                    this.summary = {
                        total_payout: Number(row.total_payout) || 0,
                        total_fees: Number(row.total_fees) || 0,
                        listing_count: Number(row.listing_count) || 0,
                    }
                    return
                }
                // fallback: compute from current page + a wider pull (simple approach)
                await this.computeSummaryFallback()
            } catch (err) {
                console.warn('Summary RPC missing; using fallback.', err)
                await this.computeSummaryFallback()
            } finally {
                this.isLoading.summary = false
            }
        },

        async computeSummaryFallback() {
            // Pull up to 10k rows and sum client-side. Replace with proper RPC when convenient.
            const { data, error } = await supabase
                .from('listings')
                // TODO: if your column isn't agent_id, change this filter.
                .select('id,total_cost,service_fee,status')
                .eq('agent_id', this.uid)
                .limit(10000)

            if (error) {
                console.error('computeSummaryFallback error:', error)
                this.summary = { total_payout: 0, total_fees: 0, listing_count: 0 }
                return
            }

            const rows = data || []
            const relevant = rows // optionally: filter by status
            const totalFees = relevant.reduce((s, r) => s + (Number(r.service_fee) || 0), 0)
            const totalPayout = relevant.reduce((s, r) => {
                const t = Number(r.total_cost) || 0
                const f = Number(r.service_fee) || 0
                return s + Math.max(0, t - f)
            }, 0)

            this.summary = { total_payout: totalPayout, total_fees: totalFees, listing_count: relevant.length }
        },

        async fetchListings(page = 1) {
            this.isLoading.listings = true
            try {
                const from = (page - 1) * this.listPageSize
                const to = from + this.listPageSize - 1

                const { data, count, error } = await supabase
                    .from('listings')
                    // TODO: if your schema uses another field, change 'agent_id' below.
                    .select('*', { count: 'exact' })
                    .eq('uid', this.uid)
                    .order('created_at', { ascending: false })
                    .range(from, to)

                if (error) throw error

                this.listings = data || []
                this.listingsCount = count ?? this.listings.length
                this.listTotalPages = Math.max(1, Math.ceil((this.listingsCount || 0) / this.listPageSize))
                this.listPage = page
            } catch (err) {
                console.error('fetchListings error:', err)
                this.listings = []
                this.listingsCount = 0
                this.listTotalPages = 1
            } finally {
                this.isLoading.listings = false
            }
        },
    },
    async mounted() {
        await this.load()
    },
    watch: {
        '$route.params.uid': {
            immediate: false,
            async handler() { await this.load() }
        }
    }
}
</script>

<style scoped>
/* minor polish */
.v-table thead th {
    font-weight: 600;
}

/* Prevent any horizontal gap from pushing cols to a new line */
.docs-row {
    column-gap: 0 !important;
    /* kill leftover ga-* / global gap */
    gap: 0 !important;
    /* safety: zero out both axes */
    row-gap: 16px;
    /* vertical spacing only */
}

/* Restore normal Vuetify side gutters inside the row */
.docs-row>.v-col {
    padding-left: 12px;
    padding-right: 12px;
}
</style>
